import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, ErrorState, EmptyState, Avatar } from "@/components/common";
import { brl, num, fmtDate } from "@/lib/format";
import { Wallet, AlertTriangle, ChevronRight, Users, Users2, MessageSquareWarning } from "@/components/icons";
import SemInteracao from "@/pages/SemInteracao";

const FILTROS = [
  { k: "", label: "Todos" },
  { k: "recente", label: "Com atividade recente" },
  { k: "sem_proxima", label: "Sem próxima ação" },
  { k: "atencao", label: "Requer atenção" },
];

const atencaoTone = (lvl) => (lvl === "critical" ? "bg-bad/15 text-bad" : lvl === "warning" ? "bg-warn/15 text-warn" : "");

export default function Carteira() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isManager = ["admin", "director", "manager", "diretor_geral"].includes(user?.role);
  const canNudge = ["admin", "director", "manager"].includes(user?.role);
  useEffect(() => { if (user && !isManager) navigate("/clientes", { replace: true }); }, [user, isManager, navigate]);
  const [vendedores, setVendedores] = useState([]);
  const [owner, setOwner] = useState(searchParams.get("owner_id") || "");
  const [filtro, setFiltro] = useState(searchParams.get("filtro") || "");
  const [visao, setVisao] = useState(searchParams.get("visao") === "sem-interacao" ? "sem-interacao" : "clientes");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // LOTE UX-2B item 1: contagem de "Sem interação" para o label da aba.
  const [semIntTotal, setSemIntTotal] = useState(null);
  useEffect(() => {
    if (!canNudge) return;
    api.get("/pipeline/sem-interacao")
      .then((r) => setSemIntTotal(typeof r.data?.total === "number" ? r.data.total : null))
      .catch(() => setSemIntTotal(null));
  }, [canNudge]);

  // Reflete a aba na URL para deep-link (ex.: /carteira?visao=sem-interacao).
  useEffect(() => {
    const sp = new URLSearchParams(searchParams);
    if (visao === "sem-interacao") sp.set("visao", "sem-interacao"); else sp.delete("visao");
    if (owner) sp.set("owner_id", owner); else sp.delete("owner_id");
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visao, owner]);

  useEffect(() => {
    if (!isManager) return;
    api.get("/carteira/vendedores").then((r) => setVendedores(r.data || [])).catch(() => {});
  }, [isManager]);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const params = new URLSearchParams();
      if (owner) params.set("owner_id", owner);
      if (filtro) params.set("filtro", filtro);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/carteira?${params.toString()}`);
      setData(data);
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [owner, filtro, q]);

  useEffect(() => { load(); }, [load]);

  const resumo = data?.resumo || {};
  const items = data?.items || [];

  return (
    <div>
      <PageHeader title="Carteira da equipe" subtitle="Acompanhe a carteira de cada vendedor e identifique clientes esquecidos" />

      {/* Aba: visão da tela — Clientes (default) vs Oportunidades sem interação. */}
      <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-xl inline-flex mb-4" role="tablist" data-testid="carteira-tabs">
        <button type="button" role="tab" onClick={() => setVisao("clientes")} data-testid="tab-clientes"
          className={`px-3 h-9 rounded-lg text-sm font-medium flex items-center gap-1.5 transition ${visao === "clientes" ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-navy"}`}>
          <Users2 className="w-4 h-4" /> Clientes
        </button>
        {canNudge && (
          <button type="button" role="tab" onClick={() => setVisao("sem-interacao")} data-testid="tab-sem-interacao"
            className={`px-3 h-9 rounded-lg text-sm font-medium flex items-center gap-1.5 transition ${visao === "sem-interacao" ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-navy"}`}>
            <MessageSquareWarning className="w-4 h-4" />
            Sem interação{semIntTotal != null ? ` (${semIntTotal})` : ""}
          </button>
        )}
      </div>

      {isManager && vendedores.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-3 mb-2 no-scrollbar" data-testid="carteira-vendedores">
          <button onClick={() => setOwner("")} data-testid="vend-todos"
            className={`shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-colors ${owner === "" ? "border-brand bg-brand-soft/50 text-brand font-semibold" : "border-input bg-white text-slate-600 hover:bg-slate-50"}`}>
            <Users className="w-4 h-4" /> Toda a equipe
          </button>
          {vendedores.map((v) => (
            <button key={v.id} onClick={() => setOwner(v.id)} data-testid={`vend-${v.id}`}
              className={`shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-colors ${owner === v.id ? "border-brand bg-brand-soft/50 text-brand font-semibold" : "border-input bg-white text-slate-600 hover:bg-slate-50"}`}>
              <Avatar name={v.nome} src={v.avatar} size={22} />
              <span>{v.nome}</span>
              <span className="text-xs text-muted-foreground">({v.clientes})</span>
              {v.requer_atencao > 0 && <span className="text-[10px] bg-warn/15 text-warn rounded-full px-1.5">{v.requer_atencao}</span>}
            </button>
          ))}
        </div>
      )}

      {visao === "sem-interacao" ? (
        <SemInteracao embedded ownerFilter={owner} />
      ) : (
        <>
          {/* Bloco: Clientes que requerem atenção */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[
          { l: "Clientes", v: num(resumo.total || 0), f: "" },
          { l: "Requer atenção", v: num(resumo.requer_atencao || 0), f: "atencao", tone: "text-warn" },
          { l: "Sem próxima ação", v: num(resumo.sem_proxima_acao || 0), f: "sem_proxima", tone: "text-bad" },
          { l: "Atividade recente", v: num(resumo.com_atividade_recente || 0), f: "recente", tone: "text-ok" },
        ].map((c) => (
          <button key={c.l} onClick={() => setFiltro(c.f)} data-testid={`resumo-${c.f || "todos"}`}
            className={`text-left bg-card rounded-2xl border p-4 shadow-[0_2px_10px_rgb(0,0,0,0.03)] transition-all hover:-translate-y-0.5 ${filtro === c.f ? "border-brand ring-1 ring-brand/30" : "border-black/5"}`}>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{c.l}</div>
            <div className={`text-2xl font-bold mt-1 ${c.tone || "text-navy"}`}>{c.v}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente..." data-testid="carteira-q"
          className="h-10 px-3 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40 min-w-[200px]" />
        {FILTROS.map((f) => (
          <button key={f.k} onClick={() => setFiltro(f.k)} data-testid={`filtro-${f.k || "todos"}`}
            className={`h-10 px-3 rounded-lg border text-sm transition-colors ${filtro === f.k ? "border-brand bg-brand-soft/50 text-brand font-semibold" : "border-input bg-white text-slate-600 hover:bg-slate-50"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? <Loading /> : error ? <ErrorState onRetry={load} /> : items.length === 0 ? (
        <EmptyState icon={Wallet} title="Nenhum cliente nesta visão" subtitle="Ajuste os filtros ou selecione outro vendedor." />
      ) : (
        <div className="space-y-2" data-testid="carteira-lista">
          {items.map((c) => (
            <div key={c.cliente_id} onClick={() => navigate(`/cliente/${c.cliente_id}`)} data-testid={`carteira-cliente-${c.cliente_id}`}
              className="bg-card rounded-xl border border-black/5 p-4 flex items-center gap-4 cursor-pointer hover:bg-slate-50 transition-colors shadow-[0_2px_10px_rgb(0,0,0,0.03)]">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-navy truncate">{(c.nome_fantasia && c.nome_fantasia.trim()) || c.nome}</span>
                  {c.requer_atencao && (
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${atencaoTone(c.requer_atencao)}`} data-testid={`atencao-${c.cliente_id}`}>
                      <AlertTriangle className="w-3 h-3" /> Requer atenção
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">· {c.situacao}</span>
                </div>
                {c.nome_fantasia && c.nome_fantasia.trim() && (c.razao_social || c.nome) && (c.razao_social || c.nome) !== c.nome_fantasia && (
                  <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{c.razao_social || c.nome}</div>
                )}
                <div className="text-xs text-muted-foreground mt-1">
                  Resp.: <span className="text-slate-600 font-medium">{c.owner_name || "—"}</span>
                  {" · "}
                  {c.dias_sem_atividade != null ? `${c.dias_sem_atividade} dias sem atividade` : "sem atividade registrada"}
                </div>
                <div className="text-xs mt-1">
                  {c.proxima_acao
                    ? <span className="text-ok">Próxima ação: {c.proxima_acao.tipo} · {fmtDate(c.proxima_acao.data)}</span>
                    : <span className="text-bad font-medium">Sem próxima ação</span>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-semibold text-navy">{c.oportunidades_abertas} op.</div>
                <div className="text-xs text-muted-foreground">{brl(c.valor_aberto)}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
            </div>
          ))}
        </div>
      )}
        </>
      )}
    </div>
  );
}
